import { encodeBase64 } from "@std/encoding/base64";
import { z } from "zod";
import { dateFormat } from "../helpers/datetime.js";
import { FetcherError } from "../helpers/fetcher.js";
import { ApiBase, } from "../helpers/fetcher_api_base.js";
import { Company, CompanyCustom, Consumption, Customer, CustomerCustom, Employee, GasPrice, Interaction, ListResponse, MeterReading, PdfDocument, ReadResponse, SalesCreditMemo, SalesCreditMemoCustom, SalesCreditMemoLine, SalesInvoice, SalesInvoiceCustom, SalesInvoiceLine, ServiceReport, WithOdataEtag, } from "../models/business_central.js";
export * from "../models/business_central.js";
/**
 * Business Central Fetch Types
 */
export const LoginRequestBody = z.object({
    grant_type: z.string(),
    scope: z.string(),
    client_id: z.string(),
    client_secret: z.string(),
});
export const LoginResponseBody = z.object({
    token_type: z.literal("Bearer"),
    expires_in: z.number(),
    ext_expires_in: z.number(),
    access_token: z.string(),
});
export const ListCompaniesResponseBody = ListResponse(Company);
export const ListCompaniesCustomResponseBody = ListResponse(CompanyCustom);
export const ListCompanyCustomersResponseBody = ListResponse(Customer);
export const ListCompanyCustomersCustomResponseBody = ListResponse(CustomerCustom);
export const ReadCompanyCustomerResponseBody = ReadResponse(Customer);
export const ReadCompanyCustomerCustomResponseBody = ReadResponse(CustomerCustom);
export const ReadCompanyCustomerByEmailResponseBody = Customer.optional();
export const UpdateCompanyCustomerRequestBody = CustomerCustom.pick({
    formasjonPhoneNo: true,
    email: true,
}).partial();
export const UpdateCompanyCustomerResponseBody = ReadResponse(ReadCompanyCustomerResponseBody);
export const ListCompanyEmployeesResponseBody = ListResponse(Employee);
export const ReadCompanyEmployeeResponseBody = ReadResponse(Employee);
export const ReadCompanyEmployeeByEmailResponseBody = Employee.optional();
export const ListCompanySalesInvoicesResponseBody = ListResponse(SalesInvoice);
export const ReadCompanySalesInvoiceResponseBody = ReadResponse(SalesInvoice);
export const ListCompanySalesInvoicesCustomResponseBody = ListResponse(SalesInvoiceCustom);
export const ReadCompanySalesInvoiceCustomResponseBody = ReadResponse(SalesInvoiceCustom).extend({
    salesInvoiceLines: WithOdataEtag(SalesInvoiceLine),
});
export const ListCompanySalesInvoiceLinesResponseBody = ListResponse(SalesInvoiceLine);
export const ReadCompanySalesInvoicePdfDocumentResponseBody = ReadResponse(PdfDocument);
export const ReadCompanySalesInvoicePdfDocumentContentResponseBody = z.string();
export const ListCompanySalesCreditMemosResponseBody = ListResponse(SalesCreditMemo);
export const ReadCompanySalesCreditMemoResponseBody = ReadResponse(SalesCreditMemo);
export const ListCompanySalesCreditMemosCustomResponseBody = ListResponse(SalesCreditMemoCustom);
export const ReadCompanySalesCreditMemoCustomResponseBody = ReadResponse(SalesCreditMemoCustom).extend({
    salesCreditMemoLines: WithOdataEtag(SalesCreditMemoLine),
});
export const ListCompanySalesCreditMemoLinesResponseBody = ListResponse(SalesCreditMemoLine);
export const ReadCompanySalesCreditMemoPdfDocumentResponseBody = ReadResponse(PdfDocument);
export const ReadCompanySalesCreditMemoPdfDocumentContentResponseBody = z.string();
export const ListCompanyMeterReadingsResponseBody = ListResponse(MeterReading);
export const CreateCompanyMeterReadingRequestBody = MeterReading.partial().omit({ id: true });
export const CreateCompanyMeterReadingResponseBody = ReadResponse(MeterReading);
export const ListCompanyConsumptionsResponseBody = ListResponse(Consumption);
export const ListCompanyInteractionsResponseBody = ListResponse(Interaction);
export const ListCompanyGasPricesResponseBody = ListResponse(GasPrice);
export const ListCustomerServiceReportsResponseBody = ListResponse(ServiceReport);
export const ReadCustomerServiceReportResponseBody = ListResponse(ServiceReport);
export class BusinessCentralApi extends ApiBase {
    urlAuth;
    urlApi;
    urlCustomApi;
    urlDataProApi;
    clientId;
    clientSecret;
    companyId;
    constructor(options) {
        super();
        this.urlAuth = options.urlAuth;
        this.urlApi = options.urlApi;
        this.urlCustomApi = options.urlCustomApi;
        this.urlDataProApi = options.urlDataProApi;
        this.clientId = options.clientId;
        this.clientSecret = options.clientSecret;
        this.companyId = options.companyId;
    }
    _parseUrl(input, options) {
        let baseUrl = options?.baseUrl;
        if (!baseUrl) {
            switch (options?.useUrl) {
                case "auth":
                    baseUrl = this.urlAuth;
                    break;
                case "customApi":
                    baseUrl = this.urlCustomApi;
                    break;
                case "dataProApi":
                    baseUrl = this.urlDataProApi;
                    break;
                default:
                    baseUrl = this.urlApi;
                    break;
            }
        }
        const url = new URL(baseUrl);
        url.pathname += input;
        return url.toString();
    }
    requester(input, options) {
        return super.requester(input, options);
    }
    get(input, options) {
        return super.get(input, options);
    }
    post(input, body, options) {
        return super.post(input, body, options);
    }
    put(input, body, options) {
        return super.put(input, body, options);
    }
    patch(input, body, options) {
        return super.patch(input, body, options);
    }
    delete(input, options) {
        return super.delete(input, options);
    }
    login({ body, options }) {
        const formData = Object.entries(body).reduce((acc, [key, value]) => {
            acc.append(key, value);
            return acc;
        }, new FormData());
        return this.post("/token", formData, {
            useUrl: "auth",
            ...options,
        });
    }
    listCompanies({ options }) {
        return this.get("/companies()", {
            useUrl: "customApi",
            ...options,
        });
    }
    listCompanyCustomers({ companyId = this.companyId, options = {}, }) {
        return this.get(`/companies(${companyId})/customers`, { useUrl: "customApi", ...options });
    }
    listCompanyCustomersByEmail(email) {
        return this.listCompanyCustomers({
            options: {
                parameters: {
                    $filter: `tolower(email) eq '${email}' and relocated eq false`,
                },
            },
        });
    }
    listCompanyCustomersByNumber(number) {
        return this.listCompanyCustomers({
            options: {
                parameters: {
                    $filter: `formasjonPhoneNo eq '${number}' and relocated eq false`,
                },
            },
        });
    }
    readCompanyCustomer({ companyId = this.companyId, customerId, options, }) {
        return this.get(`/companies(${companyId})/customers(${customerId})`, options);
    }
    readCompanyCustomerCustom({ companyId = this.companyId, customerId, options, }) {
        return this.get(`/companies(${companyId})/customers(${customerId})`, { useUrl: "customApi", ...options });
    }
    async readCompanyCustomerByEmail({ companyId = this.companyId, customerEmail, options, }) {
        const res = await this.listCompanyCustomers({
            companyId,
            options: {
                parameters: {
                    $filter: `tolower(email) eq '${customerEmail}' and relocated eq false`,
                },
                ...options,
            },
        });
        return res.value[0];
    }
    updateCompanyCustomer({ companyId = this.companyId, customerId, body, options, }) {
        return this.patch(`/companies(${companyId})/customers(${customerId})`, JSON.stringify(body), {
            useUrl: "customApi",
            headers: {
                "If-Match": "*",
                "Content-Type": "application/json",
            },
            ...options,
        });
    }
    listCompanyEmployees({ companyId = this.companyId, options, }) {
        return this.get(`/companies(${companyId})/employees`, options);
    }
    readCompanyEmployee({ companyId = this.companyId, employeeId, options, }) {
        return this.get(`/companies(${companyId})/employees(${employeeId})`, options);
    }
    async readCompanyEmployeeByEmail({ companyId = this.companyId, employeeEmail, options, }) {
        const res = await this.listCompanyEmployees({
            companyId,
            options: {
                parameters: {
                    $filter: `tolower(email) eq '${employeeEmail}'`,
                },
                ...options,
            },
        });
        return res.value[0];
    }
    listCompanySalesInvoices({ companyId = this.companyId, customerId, invoiceDateFrom = dateFormat(), options, }) {
        return this.get(`/companies(${companyId})/salesInvoices`, {
            parameters: {
                $filter: `customerId eq ${customerId} and invoiceDate gt ${invoiceDateFrom}`,
            },
            ...options,
        });
    }
    readCompanySalesInvoice({ companyId = this.companyId, salesInvoiceId, options, }) {
        return this.get(`/companies(${companyId})/salesInvoices(${salesInvoiceId})`, options);
    }
    listCompanySalesInvoicesCustom({ companyId = this.companyId, customerId, invoiceDateFrom = dateFormat(), options, }) {
        return this.get(`/companies(${companyId})/salesInvoices2`, {
            useUrl: "customApi",
            parameters: {
                $filter: `customerId eq ${customerId} and invoiceDate gt ${invoiceDateFrom}`,
            },
            ...options,
        });
    }
    readCompanySalesInvoiceCustom({ companyId = this.companyId, salesInvoiceId, options, }) {
        return this.get(`/companies(${companyId})/salesInvoices2(${salesInvoiceId})`, {
            useUrl: "customApi",
            parameters: {
                $expand: `salesInvoiceLines`,
            },
            ...options,
        });
    }
    listCompanySalesInvoiceLines({ companyId = this.companyId, salesInvoiceId, options, }) {
        return this.get(`/companies(${companyId})/salesInvoices(${salesInvoiceId})/salesInvoiceLines`, options);
    }
    readCompanySalesInvoicePdfDocument({ companyId = this.companyId, salesInvoiceId, options, }) {
        return this.get(`/companies(${companyId})/salesInvoices(${salesInvoiceId})/pdfDocument`, options);
    }
    async readCompanySalesInvoicePdfDocumentContent({ companyId = this.companyId, salesInvoiceId, options, }) {
        const url = this._parseUrl(`/companies(${companyId})/salesInvoices(${salesInvoiceId})/pdfDocument/pdfDocumentContent`, options);
        const parsedOptions = { ...options };
        this.addTokenToOptions(parsedOptions);
        const request = new Request(url, {
            ...parsedOptions,
        });
        const response = await fetch(request);
        if (!response.ok) {
            throw await FetcherError.parseFromResponse(response);
        }
        const pdf = new Uint8Array(await response.arrayBuffer());
        const base64Encoded = encodeBase64(pdf);
        return base64Encoded;
    }
    listCompanySalesCreditMemos({ companyId = this.companyId, customerId, invoiceDateFrom, options, }) {
        return this.get(`/companies(${companyId})/salesCreditMemos`, {
            parameters: {
                $filter: `customerId eq ${customerId} and dueDate gt ${invoiceDateFrom}`,
            },
            ...options,
        });
    }
    readCompanySalesCreditMemo({ companyId = this.companyId, salesCreditMemoId, options, }) {
        return this.get(`/companies(${companyId})/salesCreditMemos(${salesCreditMemoId})`, options);
    }
    listCompanySalesCreditMemosCustom({ companyId = this.companyId, customerId, invoiceDateFrom, options, }) {
        return this.get(`/companies(${companyId})/salesCreditMemos2`, {
            useUrl: "customApi",
            parameters: {
                $filter: `customerId eq ${customerId} and dueDate gt ${invoiceDateFrom}`,
            },
            ...options,
        });
    }
    readCompanySalesCreditMemoCustom({ companyId = this.companyId, salesCreditMemoId, options, }) {
        return this.get(`/companies(${companyId})/salesCreditMemos2(${salesCreditMemoId})`, {
            useUrl: "customApi",
            parameters: {
                $expand: `salesCreditMemoLines`,
            },
            ...options,
        });
    }
    listCompanySalesCreditMemoLines({ companyId = this.companyId, salesCreditMemoId, options, }) {
        return this.get(`/companies(${companyId})/salesCreditMemos(${salesCreditMemoId})/salesCreditMemoLines`, options);
    }
    readCompanySalesCreditMemoPdfDocument({ companyId = this.companyId, salesCreditMemoId, options, }) {
        return this.get(`/companies(${companyId})/salesCreditMemos(${salesCreditMemoId})/pdfDocument`, options);
    }
    async readCompanySalesCreditMemoPdfDocumentContent({ companyId = this.companyId, salesCreditMemoId, options, }) {
        const url = this._parseUrl(`/companies(${companyId})/salesCreditMemos(${salesCreditMemoId})/pdfDocument/pdfDocumentContent`, options);
        const parsedOptions = { ...options };
        this.addTokenToOptions(parsedOptions);
        const request = new Request(url, {
            ...parsedOptions,
        });
        const response = await fetch(request);
        if (!response.ok) {
            throw await FetcherError.parseFromResponse(response);
        }
        const pdf = new Uint8Array(await response.arrayBuffer());
        const base64Encoded = encodeBase64(pdf);
        return base64Encoded;
    }
    getPdfUrl({ companyId = this.companyId, documentId, documentType, options, }) {
        return this._parseUrl(`/companies(${companyId})/${documentType}(${documentId})/pdfDocument/pdfDocumentContent`, options);
    }
    /**
     * Used to list meter readings or message history entries that are not parsed/handeled.
     * Not to be used for fetching meter readings use {@link listCompanyConsumptions} instead
     */
    listCompanyMeterReadings({ companyId = this.companyId, customerNo, dateFrom, dateTo, options, }) {
        return this.get(`/companies(${companyId})/meterreadings`, {
            parameters: {
                $filter: `customerNo eq '${customerNo}' and date gt ${dateFrom} and date lt ${dateTo}`,
            },
            useUrl: "customApi",
            ...options,
        });
    }
    /**
     * Used to create a meter reading or a message history entry
     */
    createCompanyMeterReading({ companyId = this.companyId, body, options, }) {
        return this.post(`/companies(${companyId})/meterreadings`, JSON.stringify(body), {
            useUrl: "customApi",
            headers: { "Content-Type": "application/json" },
            ...options,
        });
    }
    /**
     * Used to list company consumptions over time
     */
    listCompanyConsumptions({ companyId = this.companyId, customerNo, dateFrom, dateTo, options, }) {
        return this.get(`/companies(${companyId})/consumptions`, {
            parameters: {
                $filter: `customerNo eq '${customerNo}' and readingDate gt ${dateFrom} and readingDate lt ${dateTo}`,
            },
            useUrl: "customApi",
            ...options,
        });
    }
    /**
     * Used to list company interactions over time
     */
    listCompanyInteractions({ companyId = this.companyId, customerNo, dateFrom, dateTo, options, }) {
        return this.get(`/companies(${companyId})/interactions`, {
            parameters: {
                $filter: `dnoCustomerNo eq '${customerNo}' and date gt ${dateFrom} and date lt ${dateTo}`,
            },
            useUrl: "customApi",
            ...options,
        });
    }
    /**
     * Used to list company gas prices over time
     */
    listCompanyGasPrices({ companyId = this.companyId, Avdelingskode, dateFrom, dateTo, options, }) {
        return this.get(`/companies(${companyId})/gasprices`, {
            parameters: {
                $filter: `Avdelingskode eq '${Avdelingskode}' and Dato gt ${dateFrom} and Dato lt ${dateTo}`,
            },
            useUrl: "customApi",
            ...options,
        });
    }
    /**
     * Used to list all of a customers service reports without pdfs
     */
    listCustomerServiceReports({ companyId = this.companyId, Kundenummer, pagination, options, }) {
        return this.get(`/companies(${companyId})/servicereports`, {
            parameters: {
                $filter: `Kundenummer eq '${Kundenummer}'`,
                $orderby: "dato desc",
                $top: pagination?.take ?? null,
                $skip: pagination?.skip ?? null,
            },
            useUrl: "dataProApi",
            ...options,
        });
    }
    /**
     * Used to get a customers service report with a pdf
     */
    async readCustomerServiceReport({ companyId = this.companyId, SystemId, options, }) {
        const response = await this.get(`/companies(${companyId})/servicereports`, {
            parameters: {
                $filter: `SystemId eq ${SystemId}`,
            },
            useUrl: "dataProApi",
            ...options,
        });
        return response.value[0];
    }
}
